import { useState, type CSSProperties } from 'react';
import { Badge, Mono, markColor } from './primitives';
import { Card, PeriodStepper } from './card';
import { DayMarkKey, MonthGrid, dayMarkStyle } from './monthGrid';
import type { Holiday, HolidayKind } from '../lib/holidays';
import { isWorkday, type Workdays } from '../lib/workdays';
import { Dialog } from './dialog';
import { fmtDayKey, isoDay } from '../lib/dates';
import { useBelow } from '../lib/useMediaQuery';

// ── INR + masking helpers ────────────────────────────────────────────────────
// The formatters live in lib/money.ts (sign-aware since HRA-11); re-exported so
// the payroll register and PeopleHRScreen keep importing them from here.
export { fmtINR, fmtINRFull } from '../lib/money';
/**
 * Reads a rupee amount typed the way this app prints one: whole rupees ("1450000",
 * "14,50,000", "₹1,450,000") or the shorthands fmtINR itself shows ("14.5 L",
 * "1.2 Cr", "3 lakh"). Anything else is null — never a guess. The CTC field used
 * to strip every non-digit, so "14.5 L" saved ₹145 and "1450000.50" saved
 * ₹14,50,00,050.
 *
 * A bare decimal ("14.5") is refused rather than read either way: as rupees it is
 * not a salary, and treating it as lakh would be the same guess again.
 */
export const parseINR = (input: string): number | null => {
  const s = input.trim().replace(/^₹\s*/, '');
  const short = /^(\d+(?:\.\d+)?)\s*(l|lakhs?|cr|crores?)$/i.exec(s);
  // Math.round: 14.35 * 1e5 is 1434999.9999999998 in floating point.
  const n = short
    ? Math.round(Number(short[1]) * (/^c/i.test(short[2]) ? 1e7 : 1e5))
    : // Commas only as real grouping — Indian (14,50,000) or western (1,450,000) —
      // so a stray "1,4,5" is refused rather than read as ₹145.
      /^(\d+|\d{1,3}(,\d{2})*,\d{3}|\d{1,3}(,\d{3})+)$/.test(s)
      ? Number(s.replace(/,/g, ''))
      : 0;
  // Safe-integer bound: annual_ctc is a bigint, and past 2^53 the number the
  // field shows is no longer the number that would be written.
  return n > 0 && Number.isSafeInteger(n) ? n : null;
};
export const maskAcct = (no?: string | null): string => (no ? no.replace(/.(?=.{4})/g, '•') : '');

// ── Card section with an uppercase label header ──────────────────────────────
// Moved to components/card.tsx as Card, with its label as a real h2 (DS-18,
// ME-V2). The old name stays so its 74 call sites need no churn.
export const PCard = Card;

/**
 * The marker on a card that holds private data: who can see it, in a card's
 * `right` slot. It used to be an amber "confidential" Badge on Contact and
 * Bank and nothing at all on Compensation: amber is the queue tone
 * (UI-CONVENTIONS §1), so a private card read as one waiting on somebody, and
 * the most sensitive card carried no mark (HRP-19).
 *
 * One phrasing everywhere (critique 2026-10-05 #7): "Only …", naming the
 * audience RLS grants, not "confidential" or "private", which name no one.
 * "Admins" is every role with full People access: has_perm('people','full')
 * gates contact, bank and compensation (contact_rw, bank_rw, comp_read), and
 * the seeded matrix gives it to owner, admin and hr. "Only HR" hid the owner
 * and admins from the people whose data it is. Contact, bank and statements
 * are also read by the person they belong to, so those cards pass the label:
 * "Only you, HR and admins" on /me, "Only Elif, HR and admins" on a record.
 *
 * Words alone: the eye is the Show/Hide control beside it on Bank details, and
 * a bordered badge with an eye read as a second button.
 */
export const SensitiveMark = ({ label = 'Only HR and admins' }: { label?: string }) => (
  <Badge tone="neutral">{label}</Badge>
);

// EditableField and Stepper moved to the form layer, components/field.tsx
// (UI-REMEDIATION-PLAN Phase 2). Re-exported so existing imports keep working.
export { EditableField, Stepper } from './field';

// ── Month helpers ─────────────────────────────────────────────────────────────
export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
// WEEKDAY_LABELS and weekStartIndex moved to lib/dates.ts — an org-settings
// helper has no business living in a component file, which is how it ended up
// reaching only this one component.
/** Leading blanks + day numbers for a month grid. Exported so the Org Settings
 *  holiday calendar stops keeping its own Sunday-anchored copy. */
export const monthMatrix = (
  year: number,
  month: number,
  weekStartsOn: number,
): (number | null)[] => {
  const first = new Date(year, month, 1);
  const startDow = (first.getDay() - weekStartsOn + 7) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

export type LeaveDate = {
  date: string;
  type: string;
  reason?: string | null;
  /** 'pending' draws hollow and says so: a request still waiting was drawn
   *  exactly like approved leave (ME-12). Absent means approved. */
  status?: 'pending' | 'approved';
};
export type LeaveTypeMeta = { label: string; color: string };
/** A holiday on the calendar: its name, or its name and kind. A bare name is a
 *  read that left out `kind`, and is drawn as a holiday of no kind. */
export type LeaveCalendarHoliday = string | Pick<Holiday, 'name' | 'kind'>;

// ── Leave calendar — a month of leave days and holidays ──────────────────────
// Rebuilt on MonthGrid (UI-REMEDIATION-PLAN §4.3). The old grid's day cells were
// divs: a leave's type and reason, and a holiday's name, lived in a hover card
// no keyboard or touch screen could open (ME-V3), its prev/next buttons had no
// name (ME-V1), and fixed 34px-minimum cells pushed Saturday out of the card at
// 1280px (HRP-08). Now one Tab stop with arrow keys; a day's details are read
// with its date, and selecting it prints them under the grid.
//
// Its marks are the shared set (DayMark, monthGrid.tsx), as /me's are: a leave
// bar, filled when approved and outlined while waiting for HR; a holiday a
// square in its kind's hue, or neutral ink when it came with no kind. It drew a
// holiday as a neutral dashed pill, and on /me dashed meant "waiting for HR"
// (critique 2026-10-05 #1).
//
// A day the org doesn't work is hatched, as on the Workspace calendar: Sat and
// Sun were blank cells here, the same as a working day with nothing on it
// (critique 2026-10-06 #2). The key is the shared foot, DayMarkKey, keyed to
// what this month draws.
export const LeaveCalendar = ({
  leaveDates,
  leaveTypes,
  holidays,
  initial,
  weekStartsOn,
  today,
  workdays,
}: {
  leaveDates: LeaveDate[];
  leaveTypes: Record<string, LeaveTypeMeta>;
  // ISO date → the holiday, drawn by its kind. These never overlap a leave
  // day — leave_working_days() excludes holidays, so a leave span covering
  // one is not charged for it and does not list it.
  holidays?: Record<string, LeaveCalendarHoliday>;
  initial?: { y: number; m: number };
  /** getDay() index the week starts on (0=Sun, 1=Mon). REQUIRED on purpose: a
   *  default here is a fifth place the org setting can be silently contradicted,
   *  and a missing prop should be a compile error, not a Monday. */
  weekStartsOn: number;
  /** The org's today as 'YYYY-MM-DD' (orgDateKey). Falls back to the browser's. */
  today?: string;
  /** The org's working week (useOrgDates). Given, a day it doesn't work is
   *  hatched; `null` is the schema's default week, as isWorkday reads it. */
  workdays?: Workdays | null;
}) => {
  const todayKey = today ?? isoDay(new Date());
  const [ym, setYm] = useState(
    initial ?? { y: Number(todayKey.slice(0, 4)), m: Number(todayKey.slice(5, 7)) - 1 },
  );
  const [picked, setPicked] = useState<string | null>(null);
  // Below md the detail under the grid is past the foot of the screen, so a
  // chosen day opens in a Dialog: a bottom sheet on a phone (UI-CONVENTIONS §8).
  const phone = useBelow('md');
  // Crossing md lets the day go: kept, a day chosen on a wide window would
  // open the sheet unasked when the window narrowed or a phone turned upright.
  const [pickedOnPhone, setPickedOnPhone] = useState(phone);
  if (pickedOnPhone !== phone) {
    setPickedOnPhone(phone);
    setPicked(null);
  }
  const byDate: Record<string, LeaveDate> = {};
  leaveDates.forEach((l) => (byDate[l.date] = l));
  const nav = (d: number) =>
    setYm((p) => {
      const i = p.y * 12 + p.m + d;
      return { y: Math.floor(i / 12), m: ((i % 12) + 12) % 12 };
    });
  const monthKey = `${ym.y}-${String(ym.m + 1).padStart(2, '0')}`;
  const monthLeave = leaveDates.filter((l) => l.date.startsWith(monthKey));
  const monthCount = monthLeave.length;
  const monthDays = Array.from(
    { length: new Date(ym.y, ym.m + 1, 0).getDate() },
    (_, i) => `${monthKey}-${String(i + 1).padStart(2, '0')}`,
  );
  const monthHolidays = monthDays.flatMap((k) => (holidays?.[k] ? [holidays[k]] : []));

  const holidayOf = (key: string): { name: string; kind?: HolidayKind } | undefined => {
    const h = holidays?.[key];
    return typeof h === 'string' ? { name: h } : h;
  };
  // A day off is the org's week alone: a holiday draws its own square.
  const offDay = (key: string) => workdays !== undefined && !isWorkday(key, workdays);

  // Leave wins if both somehow land on a day; a holiday is never charged.
  const dayInfo = (key: string): { mark: CSSProperties; text: string } | null => {
    const rec = byDate[key];
    if (rec) {
      const t = leaveTypes[rec.type];
      const pending = rec.status === 'pending';
      return {
        mark: dayMarkStyle(pending ? 'pending' : 'leave', t?.color ?? markColor('iris')),
        text: [
          `${t?.label ?? rec.type} leave, ${pending ? 'waiting for HR' : 'approved'}`,
          rec.reason,
        ]
          .filter(Boolean)
          .join(' — '),
      };
    }
    const hol = holidayOf(key);
    if (!hol) return offDay(key) ? { mark: dayMarkStyle('off'), text: 'Non-working day' } : null;
    return hol.kind
      ? { mark: dayMarkStyle(hol.kind), text: `${hol.name}, ${hol.kind} holiday` }
      : { mark: dayMarkStyle('holiday'), text: `${hol.name}, holiday` };
  };
  const shown = picked?.startsWith(monthKey) ? picked : null;
  const pickedInfo = shown ? dayInfo(shown) : null;
  const pickedText = pickedInfo ? pickedInfo.text : 'No leave or holiday on this day.';

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'var(--hf-space-2) var(--hf-space-3)',
          marginBottom: 'var(--hf-space-3)',
        }}
      >
        <PeriodStepper
          label={`${MONTHS[ym.m]} ${ym.y}`}
          unit="month"
          onPrev={() => nav(-1)}
          onNext={() => nav(1)}
        />
        {/* "Days off" now that the hatch is a day off: these are leave. A
            phrase, so the UI face with tabular figures, not mono. */}
        {monthCount > 0 && (
          <span
            style={{
              fontSize: 'var(--hf-text-xs)',
              color: 'var(--hf-ink-3)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {monthCount} day{monthCount > 1 ? 's' : ''} of leave
          </span>
        )}
      </div>
      <MonthGrid
        year={ym.y}
        month={ym.m}
        weekStartsOn={weekStartsOn}
        compact
        today={todayKey}
        selectedKey={picked ?? undefined}
        onSelectDay={setPicked}
        renderDay={(key) => {
          const d = dayInfo(key);
          return d
            ? [
                <span key="d" style={{ display: 'flex' }}>
                  <span aria-hidden="true" style={d.mark} />
                  <span className="hf-sr-only">{d.text}</span>
                </span>,
              ]
            : [];
        }}
      />
      {/* What the selected day holds, in words: the only place a reason or a
          holiday's name is visible without a pointer. On a phone, the sheet. */}
      <div
        aria-live={phone ? undefined : 'polite'}
        style={{
          minHeight: '1.5em',
          marginTop: 'var(--hf-space-2)',
          fontSize: 'var(--hf-text-xs)',
          lineHeight: 'var(--hf-leading-body)',
          color: 'var(--hf-ink-2)',
        }}
      >
        {shown && !phone ? (
          <>
            <Mono>{fmtDayKey(shown, { weekday: true })}</Mono>
            {' · '}
            {pickedText}
          </>
        ) : (
          <span style={{ color: 'var(--hf-ink-3)' }}>Select a day to read what is on it.</span>
        )}
      </div>
      {phone && (
        <Dialog
          open={!!shown}
          onClose={() => setPicked(null)}
          title={shown ? fmtDayKey(shown, { weekday: true }) : ''}
          size="sm"
        >
          <p
            style={{
              margin: 0,
              fontSize: 'var(--hf-text-sm)',
              lineHeight: 'var(--hf-leading-body)',
              color: 'var(--hf-ink-2)',
              overflowWrap: 'anywhere',
            }}
          >
            {pickedText}
          </p>
        </Dialog>
      )}
      {/* What this month draws, and nothing it doesn't: every leave type and
          every holiday kind of the year were keyed on every month. */}
      <DayMarkKey
        leaveTypes={Object.entries(leaveTypes)
          .filter(([id]) => monthLeave.some((l) => l.type === id))
          .map(([, t]) => t)}
        pending={monthLeave.some((l) => l.status === 'pending')}
        holidays={monthHolidays}
        off={monthDays.some((k) => !holidays?.[k] && offDay(k))}
      />
    </div>
  );
};
